/**
 * Settings › HR › Employment Rules (piece 34, *Jurisdiction*; decision 6841):
 * every `EmploymentJurisdiction` row by state, with its locality and its
 * effective-from, added and edited by an administrator. 🔒 Every column of the
 * table is a field, and a field that counts carries its unit in its label.
 * 🔨 A state code is two upper-case letters; nothing is deleted.
 */
import { isCalendarDay } from '../zonedTime';

/** The Settings page. */
export const EMPLOYMENT_RULES_ROUTE = '/settings/hr/employment-rules';
/** The list and its add, relative to the API root. */
export const EMPLOYMENT_RULES_PATH = '/hr/employment-rules';

/** One row's edit. */
export function employmentRulePath(id: string): string {
  return `${EMPLOYMENT_RULES_PATH}/${encodeURIComponent(id)}`;
}

export const SEVENTH_DAY_RULES = ['None', 'SeventhConsecutiveDay'] as const;
export type SeventhDayRuleName = (typeof SEVENTH_DAY_RULES)[number];

export const FINAL_PAY_RULES = ['NextRegularPayday', 'LastDayWorked', 'WithinDays'] as const;
export type FinalPayRuleName = (typeof FINAL_PAY_RULES)[number];

/** One row as the page lists it; the effective-from is the `YYYY-MM-DD` day the column holds. */
export interface EmploymentRuleRow {
  id: string;
  version: number;
  stateCode: string;
  locality: string | null;
  effectiveFrom: string;
  weeklyOvertimeThresholdHours: number;
  dailyOvertimeThresholdHours: number | null;
  doubleTimeThresholdHours: number | null;
  seventhDayRule: SeventhDayRuleName;
  overtimeWaiverPermitted: boolean;
  mealBreakAfterHours: number | null;
  mealBreakMinutes: number | null;
  restBreakMinutesPerHours: number | null;
  breakAttestationRequired: boolean;
  finalPayDischargeRule: FinalPayRuleName;
  finalPayDischargeDays: number | null;
  finalPayResignationRule: FinalPayRuleName;
  finalPayResignationDays: number | null;
  paidSickLeaveMandated: boolean;
  leaveMandateNote: string | null;
  unemploymentResponseDays: number;
  drugTestingConstraints: string;
  requiredPostings: string;
}

/** What the window sends: every number as the field holds it, a blank optional one meaning none. */
export interface EmploymentRuleDraft {
  stateCode: string;
  locality: string;
  effectiveFrom: string;
  weeklyOvertimeThresholdHours: string;
  dailyOvertimeThresholdHours: string;
  doubleTimeThresholdHours: string;
  seventhDayRule: SeventhDayRuleName;
  overtimeWaiverPermitted: boolean;
  mealBreakAfterHours: string;
  mealBreakMinutes: string;
  restBreakMinutesPerHours: string;
  breakAttestationRequired: boolean;
  finalPayDischargeRule: FinalPayRuleName;
  finalPayDischargeDays: string;
  finalPayResignationRule: FinalPayRuleName;
  finalPayResignationDays: string;
  paidSickLeaveMandated: boolean;
  leaveMandateNote: string;
  unemploymentResponseDays: string;
  drugTestingConstraints: string;
  requiredPostings: string;
}

/** The row's values as the table takes them, once the draft has passed its check. */
export type EmploymentRuleValues = Omit<EmploymentRuleRow, 'id' | 'version'>;

const text = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' ? String(value) : '');
const blank = (value: unknown): boolean => text(value).trim() === '';
const number = (value: unknown): number => Number(text(value).trim());
const STATE_CODE = /^[A-Z]{2}$/;

/** The window's draft from a row, every number as text. */
export function employmentRuleDraft(row: EmploymentRuleRow): EmploymentRuleDraft {
  const field = (value: number | null) => (value === null ? '' : String(value));
  return {
    stateCode: row.stateCode, locality: row.locality ?? '', effectiveFrom: row.effectiveFrom,
    weeklyOvertimeThresholdHours: String(row.weeklyOvertimeThresholdHours), dailyOvertimeThresholdHours: field(row.dailyOvertimeThresholdHours),
    doubleTimeThresholdHours: field(row.doubleTimeThresholdHours), seventhDayRule: row.seventhDayRule, overtimeWaiverPermitted: row.overtimeWaiverPermitted,
    mealBreakAfterHours: field(row.mealBreakAfterHours), mealBreakMinutes: field(row.mealBreakMinutes), restBreakMinutesPerHours: field(row.restBreakMinutesPerHours),
    breakAttestationRequired: row.breakAttestationRequired, finalPayDischargeRule: row.finalPayDischargeRule, finalPayDischargeDays: field(row.finalPayDischargeDays),
    finalPayResignationRule: row.finalPayResignationRule, finalPayResignationDays: field(row.finalPayResignationDays), paidSickLeaveMandated: row.paidSickLeaveMandated,
    leaveMandateNote: row.leaveMandateNote ?? '', unemploymentResponseDays: String(row.unemploymentResponseDays),
    drugTestingConstraints: row.drugTestingConstraints, requiredPostings: row.requiredPostings,
  };
}

/** An empty draft for a new state's rules, from the day given; the federal floor's choices stand as the defaults. */
export function emptyEmploymentRuleDraft(today: string): EmploymentRuleDraft {
  return {
    stateCode: '', locality: '', effectiveFrom: today, weeklyOvertimeThresholdHours: '40', dailyOvertimeThresholdHours: '', doubleTimeThresholdHours: '',
    seventhDayRule: 'None', overtimeWaiverPermitted: false, mealBreakAfterHours: '', mealBreakMinutes: '', restBreakMinutesPerHours: '', breakAttestationRequired: false,
    finalPayDischargeRule: 'NextRegularPayday', finalPayDischargeDays: '', finalPayResignationRule: 'NextRegularPayday', finalPayResignationDays: '',
    paidSickLeaveMandated: false, leaveMandateNote: '', unemploymentResponseDays: '', drugTestingConstraints: '', requiredPostings: '',
  };
}

/**
 * 🔒 Every refusal the window and the server both give (decision 6841), in a
 * sentence each. A days field counts only for a WithinDays final-pay rule.
 */
export function employmentRuleProblems(draft: EmploymentRuleDraft): string[] {
  const problems: string[] = [];
  if (!STATE_CODE.test(text(draft.stateCode).trim())) problems.push('A state code is two upper-case letters, such as PA.');
  if (!isCalendarDay(text(draft.effectiveFrom).trim())) problems.push('Employment rules take effect from a date.');
  const hours = (value: unknown, sentence: string, required = false) => {
    if (blank(value)) {
      if (required) problems.push(sentence);
      return;
    }
    const n = number(value);
    if (!Number.isFinite(n) || n <= 0) problems.push(sentence);
  };
  const whole = (value: unknown, sentence: string, required = false) => {
    if (blank(value)) {
      if (required) problems.push(sentence);
      return;
    }
    const n = number(value);
    if (!Number.isInteger(n) || n < 0) problems.push(sentence);
  };
  // 🔨 A rule outside the lists is a sentence, never the database's refusal (T3005).
  if (!(SEVENTH_DAY_RULES as readonly string[]).includes(draft.seventhDayRule)) problems.push('The seventh-day rule is None or Seventh Consecutive Day.');
  if (!(FINAL_PAY_RULES as readonly string[]).includes(draft.finalPayDischargeRule)) problems.push('Final pay after a discharge is the next regular payday, the last day worked or within days.');
  if (!(FINAL_PAY_RULES as readonly string[]).includes(draft.finalPayResignationRule)) problems.push('Final pay after a resignation is the next regular payday, the last day worked or within days.');
  hours(draft.weeklyOvertimeThresholdHours, 'The weekly overtime threshold is a number of hours above nought.', true);
  hours(draft.dailyOvertimeThresholdHours, 'A daily overtime threshold is a number of hours above nought, or left blank.');
  hours(draft.doubleTimeThresholdHours, 'A double-time threshold is a number of hours above nought, or left blank.');
  hours(draft.mealBreakAfterHours, 'A meal break is owed after a number of hours above nought, or left blank.');
  whole(draft.mealBreakMinutes, 'A meal break is a whole number of minutes, or left blank.');
  hours(draft.restBreakMinutesPerHours, 'A rest break is a number of minutes per hour worked above nought, or left blank.');
  if (draft.finalPayDischargeRule === 'WithinDays') whole(draft.finalPayDischargeDays, 'Final pay within days names the whole number of days after a discharge.', true);
  if (draft.finalPayResignationRule === 'WithinDays') whole(draft.finalPayResignationDays, 'Final pay within days names the whole number of days after a resignation.', true);
  whole(draft.unemploymentResponseDays, 'The unemployment response is a whole number of days.', true);
  if (blank(draft.drugTestingConstraints)) problems.push('The drug-testing constraints are written out, even where there are none.');
  if (blank(draft.requiredPostings)) problems.push('The required postings are written out, even where there are none.');
  return problems;
}

/** The draft as the table takes it; call it once the draft passes its check. A days field is cleared for any rule but WithinDays. */
export function employmentRuleValues(draft: EmploymentRuleDraft): EmploymentRuleValues {
  const optional = (value: unknown): number | null => (blank(value) ? null : number(value));
  const note = text(draft.leaveMandateNote).trim();
  return {
    stateCode: text(draft.stateCode).trim(), locality: text(draft.locality).trim() || null, effectiveFrom: text(draft.effectiveFrom).trim(),
    weeklyOvertimeThresholdHours: number(draft.weeklyOvertimeThresholdHours), dailyOvertimeThresholdHours: optional(draft.dailyOvertimeThresholdHours),
    doubleTimeThresholdHours: optional(draft.doubleTimeThresholdHours), seventhDayRule: draft.seventhDayRule, overtimeWaiverPermitted: draft.overtimeWaiverPermitted === true,
    mealBreakAfterHours: optional(draft.mealBreakAfterHours), mealBreakMinutes: optional(draft.mealBreakMinutes), restBreakMinutesPerHours: optional(draft.restBreakMinutesPerHours),
    breakAttestationRequired: draft.breakAttestationRequired === true,
    finalPayDischargeRule: draft.finalPayDischargeRule, finalPayDischargeDays: draft.finalPayDischargeRule === 'WithinDays' ? optional(draft.finalPayDischargeDays) : null,
    finalPayResignationRule: draft.finalPayResignationRule, finalPayResignationDays: draft.finalPayResignationRule === 'WithinDays' ? optional(draft.finalPayResignationDays) : null,
    paidSickLeaveMandated: draft.paidSickLeaveMandated === true, leaveMandateNote: note || null, unemploymentResponseDays: number(draft.unemploymentResponseDays),
    drugTestingConstraints: text(draft.drugTestingConstraints).trim(), requiredPostings: text(draft.requiredPostings).trim(),
  };
}

/** 🔨 The refusal of a second row for one state, locality and day, in the sentence the window shows. */
export function employmentRuleDuplicateRefusal(values: Pick<EmploymentRuleValues, 'stateCode' | 'locality' | 'effectiveFrom'>): string {
  const where = values.locality ? `${values.stateCode}, ${values.locality}` : values.stateCode;
  return `Employment rules for ${where} already take effect on ${values.effectiveFrom}; edit that row, or choose another date.`;
}
